import createEnumValidator from '@studio/utils/createEnumValidator';
import createLabels from '@studio/utils/createLabels';

export enum Role {
  AccountingEditor = 'accountingeditor',
  Admin = 'admin',
  AsmTester = 'asmtester',
  BadgeManager = 'badgemanager',
  BusinessReader = 'businessreader',
  CampaignManager = 'campaignmanager',
  CapabilityManager = 'capabilitymanager',
  ContentPlayer = 'contentplayer',
  ContentPromoter = 'contentpromoter',
  ContentPromoterReader = 'contentpromoterreader',
  ContentPromoterManager = 'contentpromotermanager',
  ContractManager = 'contractmanager',
  CustomerSupport = 'customersupport',
  DianaCategoryManager = 'dianacategorymanager',
  DianaOverrideManager = 'dianaoverridemanager',
  DianaOverridePublisher = 'dianaoverridepublisher',
  GprpViewer = 'gprpviewer',
  GprpOperationsManager = 'gprpoperationsmanager',
  EditorialReader = 'editorialreader',
  Editor = 'editor',
  EditorAPAC = 'editorapac',
  EditorEMEA = 'editoremea',
  EditorGlobal = 'editorglobal',
  EditorLATAM = 'editorlatam',
  EditorUCAN = 'editorucan',
  GeoOverrider = 'geooverrider',
  GSMUIApprover = 'gsmuiapprover',
  GAuthPartnerConfigAPIAdmin = 'gauthpartnerconfigapiadmin',
  GAuthPartnerConfigAPIViewer = 'gauthpartnerconfigapiviewer',
  Ingest = 'ingest',
  IpWhitelister = 'ipwhitelister',
  LabsAdmin = 'labsadmin',
  LabsManager = 'labsmanager',
  LabsViewer = 'labsviewer',
  LegalManager = 'legalmanager',
  LocalizationManager = 'localizationmanager',
  LocalizationReader = 'localizationreader',
  LocalizationWriter = 'localizationwriter',
  Masquerader = 'masquerader',
  MessagingCampaignManager = 'messagingcampaignmanager',
  MosaicViewer = 'mosaicviewer',
  MosaicDataEngineer = 'mosaicdataengineer',
  MosaicAudienceViewer = 'mosaicaudienceviewer',
  MosaicAudienceManager = 'mosaicaudiencemanager',
  MosaicCRMViewer = 'mosaiccrmviewer',
  MosaicCRMManager = 'mosaiccrmmanager',
  MultiConfigAdmin = 'multiconfigadmin',
  MultiConfigCommerceManager = 'multiconfigcommercemanager',
  MultiConfigConsentsManager = 'multiconfigconsentsmanager',
  MultiConfigContentManager = 'multiconfigcontentmanager',
  MultiConfigExperienceManager = 'multiconfigexperiencemanager',
  MultiConfigFraudManager = 'multiconfigfraudmanager',
  MultiConfigGeographicManager = 'multiconfiggeographicmanager',
  MultiConfigIdentityManager = 'multiconfigidentitymanager',
  MultiConfigLocalizationManager = 'multiconfiglocalizationmanager',
  MultiConfigMaasManager = 'multiconfigmaasmanager',
  MultiConfigMigrationManager = 'multiconfigmigrationmanager',
  MultiConfigProfileManager = 'multiconfigprofilemanager',
  MultiConfigRatingsManager = 'multiconfigratingsmanager',
  MultiConfigSettingsAdmin = 'multiconfigsettingsadmin',
  MultiConfigLanguagesManager = 'multiconfiglanguagesmanager',
  MultiConfigPlatformsManager = 'multiconfigplatformsmanager',
  MultiConfigTerritoriesManager = 'multiconfigterritoriesmanager',
  Multiverse = 'multiverse',
  MultiverseAdmin = 'multiverseadmin',
  NoRole = 'none',
  Notifier = 'notifier',
  PackageEditor = 'packageeditor',
  PartnerIntegrationManager = 'partnerintegrationmanager',
  PersonalizationSandboxUser = 'personalizationsandboxuser',
  PPVManager = 'ppvmanager',
  ProductLineEditor = 'productlineeditor',
  ProductManager = 'productmanager',
  BulkRefundManager = 'bulkrefundmanager',
  RatingsManager = 'ratingsmanager',
  RARApprover = 'rarapprover',
  SegmentManager = 'segmentmanager',
  SiteBuilderManager = 'sitebuildermanager',
  SiteConfigManager = 'siteconfigmanager',
  SonicDeveloper = 'sonicdeveloper',
  SonicStudioBetaTester = 'sonicstudiobetatester',
  StorageManager = 'storagemanager',
  StudioIdentityTester = 'studioidentitytester',
  StudioUserAdmin = 'studiouseradmin',
  TaxonomyManager = 'taxonomymanager',
  TaxonomyImageManager = 'taxonomyimagemanager',
  TemplateManager = 'templatemanager',
  TestSubscriptionsManager = 'testsubscriptionsmanager',
  UsersAdmin = 'usersadmin',
  UsersReader = 'userreader',
  WatchHistoryEditor = 'watchhistoryeditor',
  WatchHistoryReader = 'watchhistoryreader',
}

export const { getItemLabel: getRoleName, getItemKey: getRoleKey } =
  createLabels<Role>({
    [Role.AccountingEditor]: 'Accounting Editor',
    [Role.Admin]: 'Admin',
    [Role.AsmTester]: 'ASM Tester',
    [Role.BadgeManager]: 'Badge Manager',
    [Role.BusinessReader]: 'Business Reader',
    [Role.CampaignManager]: 'Campaign Manager',
    [Role.CapabilityManager]: 'Capability Manager',
    [Role.ContentPlayer]: 'Content Player',
    [Role.ContentPromoter]: 'Content Promoter',
    [Role.ContentPromoterReader]: 'Content Promoter Reader',
    [Role.ContentPromoterManager]: 'Content Promoter Manager',
    [Role.ContractManager]: 'Contract Manager',
    [Role.CustomerSupport]: 'Customer Support',
    [Role.DianaCategoryManager]: 'DIANA Category Manager',
    [Role.DianaOverrideManager]: 'DIANA Override Manager',
    [Role.DianaOverridePublisher]: 'DIANA Override Publisher',
    [Role.GprpViewer]: 'GPRP Viewer',
    [Role.GprpOperationsManager]: 'GPRP Operations Manager',
    [Role.EditorialReader]: 'Editorial Reader',
    [Role.Editor]: 'Editor',
    [Role.EditorAPAC]: 'Editor APAC',
    [Role.EditorEMEA]: 'Editor EMEA',
    [Role.EditorGlobal]: 'Editor Global',
    [Role.EditorLATAM]: 'Editor LATAM',
    [Role.EditorUCAN]: 'Editor UCAN',
    [Role.GeoOverrider]: 'Geo Overrider',
    [Role.GAuthPartnerConfigAPIAdmin]: 'GAuth Partner Config API Admin',
    [Role.GAuthPartnerConfigAPIViewer]: 'GAuth Partner Config API Viewer',
    [Role.GSMUIApprover]: 'GSM UI Approver',
    [Role.Ingest]: 'Ingest (Deprecated)',
    [Role.IpWhitelister]: 'IP Manager',
    [Role.LabsAdmin]: 'Labs Admin',
    [Role.LabsManager]: 'Labs Manager',
    [Role.LabsViewer]: 'Labs Viewer',
    [Role.LegalManager]: 'Legal Manager',
    [Role.LocalizationManager]: 'Localization Manager',
    [Role.LocalizationReader]: 'Localization Reader',
    [Role.LocalizationWriter]: 'Localization Writer',
    [Role.MosaicViewer]: 'Mosaic Viewer',
    [Role.MosaicDataEngineer]: 'Mosaic Data Engineering',
    [Role.MosaicAudienceViewer]: 'Mosaic Audience Viewer',
    [Role.MosaicAudienceManager]: 'Mosaic Audience Manager',
    [Role.MosaicCRMViewer]: 'Mosaic CRM Viewer',
    [Role.MosaicCRMManager]: 'Mosaic CRM Manager',
    [Role.MultiConfigAdmin]: 'Multiconfig Admin',
    [Role.MultiConfigCommerceManager]: 'Multiconfig Commerce Manager',
    [Role.MultiConfigConsentsManager]: 'Multiconfig Consents Manager',
    [Role.MultiConfigContentManager]: 'Multiconfig Content Manager',
    [Role.MultiConfigExperienceManager]: 'Multiconfig Experience Manager',
    [Role.MultiConfigFraudManager]: 'Multiconfig Fraud Manager',
    [Role.MultiConfigGeographicManager]: 'Multiconfig Geographic Manager',
    [Role.MultiConfigIdentityManager]: 'Multiconfig Identity Manager',
    [Role.MultiConfigLocalizationManager]: 'Multiconfig Localization Manager',
    [Role.MultiConfigMaasManager]: 'Multiconfig MAAS Manager',
    [Role.MultiConfigMigrationManager]: 'Multiconfig Migration Manager',
    [Role.MultiConfigProfileManager]: 'Multiconfig Profile Manager',
    [Role.MultiConfigRatingsManager]: 'Multiconfig Ratings Manager',
    [Role.MultiConfigSettingsAdmin]: 'Multiconfig Settings Admin',
    [Role.MultiConfigLanguagesManager]: 'Multiconfig Languages Manager',
    [Role.MultiConfigPlatformsManager]: 'Multiconfig Platforms Manager',
    [Role.MultiConfigTerritoriesManager]: 'Multiconfig Territories Manager',
    [Role.Multiverse]: 'Multiverse',
    [Role.MultiverseAdmin]: 'Multiverse Admin',
    [Role.NoRole]: 'No Role',
    [Role.Notifier]: 'Notifier (Deprecated)',
    [Role.PackageEditor]: 'Package Editor',
    [Role.PartnerIntegrationManager]: 'Partner Integration Manager',
    [Role.PersonalizationSandboxUser]: 'Personalization Sandbox User',
    [Role.PPVManager]: 'PPV Manager',
    [Role.ProductLineEditor]: 'Product Line Editor',
    [Role.ProductManager]: 'Product Manager',
    [Role.RatingsManager]: 'Ratings Manager',
    [Role.RARApprover]: 'RAR Approver',
    [Role.BulkRefundManager]: 'Bulk Refund Manager',
    [Role.SegmentManager]: 'Segment Manager',
    [Role.SiteBuilderManager]: 'Site Builder Manager',
    [Role.SiteConfigManager]: 'Site Config Manager',
    [Role.SonicDeveloper]: 'Sonic Developer',
    [Role.SonicStudioBetaTester]: 'Sonic Studio Beta Tester',
    [Role.StorageManager]: 'Storage Manager',
    [Role.StudioIdentityTester]: 'Studio Identity Tester',
    [Role.StudioUserAdmin]: 'Studio User Admin',
    [Role.TaxonomyManager]: 'Taxonomy Manager',
    [Role.TemplateManager]: 'Template Manager',
    [Role.TestSubscriptionsManager]: 'Test Subscriptions Manager',
    [Role.UsersAdmin]: 'User Admin',
    [Role.UsersReader]: 'User Read Only',
    [Role.WatchHistoryEditor]: 'Watch History Editor',
    [Role.WatchHistoryReader]: 'Watch History Reader',
    [Role.Masquerader]: 'Masquerader',
  });

export const ALL_ROLES = Object.values(Role).sort() as readonly Role[];

export const isRole = createEnumValidator(Role);

export enum RoleOwner {
  Ananda = 'Ananda Sinha',
  Anton = 'Anton Westman',
  Christy = 'Christy Miller',
  Deepna = 'Deepna Devkar',
  Deprecated = 'Deprecated',
  Eric = 'Eric Hernandez',
  Jim = 'Jim Anderson',
  Jonas = 'Jonas Karstrom',
  Peder = 'Peder Krogh',
  Shilan = 'Shilan Heravi',
  Stephen = 'Stephen Samiya',
  Ravi = 'Ravi Chidambaram',
  Victoria = 'Victoria Breeden',
}

export const { getItemLabel: getRoleOwner } = createLabels<Role>({
  [Role.AccountingEditor]: RoleOwner.Peder,
  [Role.Admin]: RoleOwner.Anton,
  [Role.AsmTester]: RoleOwner.Ravi,
  [Role.BusinessReader]: RoleOwner.Peder,
  [Role.CampaignManager]: RoleOwner.Peder,
  [Role.CapabilityManager]: RoleOwner.Peder,
  [Role.ContentPlayer]: RoleOwner.Christy,
  [Role.ContentPromoter]: RoleOwner.Christy,
  [Role.ContentPromoterReader]: RoleOwner.Christy,
  [Role.ContentPromoterManager]: RoleOwner.Christy,
  [Role.ContractManager]: RoleOwner.Peder,
  [Role.CustomerSupport]: RoleOwner.Eric,
  [Role.DianaCategoryManager]: RoleOwner.Ananda,
  [Role.DianaOverrideManager]: RoleOwner.Ananda,
  [Role.DianaOverridePublisher]: RoleOwner.Ananda,
  [Role.GprpViewer]: RoleOwner.Ravi,
  [Role.GprpOperationsManager]: RoleOwner.Ravi,
  [Role.EditorialReader]: RoleOwner.Christy,
  [Role.Editor]: RoleOwner.Christy,
  [Role.EditorAPAC]: RoleOwner.Christy,
  [Role.EditorEMEA]: RoleOwner.Christy,
  [Role.EditorGlobal]: RoleOwner.Christy,
  [Role.EditorLATAM]: RoleOwner.Christy,
  [Role.EditorUCAN]: RoleOwner.Christy,
  [Role.GeoOverrider]: RoleOwner.Eric,
  [Role.GAuthPartnerConfigAPIAdmin]: RoleOwner.Anton,
  [Role.GAuthPartnerConfigAPIViewer]: RoleOwner.Anton,
  [Role.GSMUIApprover]: RoleOwner.Anton,
  [Role.Ingest]: RoleOwner.Deprecated,
  [Role.IpWhitelister]: RoleOwner.Shilan,
  [Role.LabsAdmin]: RoleOwner.Deepna,
  [Role.LabsManager]: RoleOwner.Deepna,
  [Role.LabsViewer]: RoleOwner.Deepna,
  [Role.LegalManager]: RoleOwner.Jim,
  [Role.LocalizationManager]: RoleOwner.Anton,
  [Role.LocalizationReader]: RoleOwner.Anton,
  [Role.LocalizationWriter]: RoleOwner.Anton,
  [Role.Masquerader]: RoleOwner.Anton,
  [Role.MosaicViewer]: RoleOwner.Stephen,
  [Role.MosaicDataEngineer]: RoleOwner.Stephen,
  [Role.MosaicAudienceViewer]: RoleOwner.Stephen,
  [Role.MosaicAudienceManager]: RoleOwner.Stephen,
  [Role.MosaicCRMViewer]: RoleOwner.Stephen,
  [Role.MosaicCRMManager]: RoleOwner.Stephen,
  [Role.MultiConfigAdmin]: RoleOwner.Ananda,
  [Role.MultiConfigCommerceManager]: RoleOwner.Ananda,
  [Role.MultiConfigConsentsManager]: RoleOwner.Ananda,
  [Role.MultiConfigContentManager]: RoleOwner.Ananda,
  [Role.MultiConfigExperienceManager]: RoleOwner.Ananda,
  [Role.MultiConfigFraudManager]: RoleOwner.Ananda,
  [Role.MultiConfigGeographicManager]: RoleOwner.Ananda,
  [Role.MultiConfigIdentityManager]: RoleOwner.Ananda,
  [Role.MultiConfigLocalizationManager]: RoleOwner.Ananda,
  [Role.MultiConfigMaasManager]: RoleOwner.Ananda,
  [Role.MultiConfigMigrationManager]: RoleOwner.Ananda,
  [Role.MultiConfigProfileManager]: RoleOwner.Ananda,
  [Role.MultiConfigRatingsManager]: RoleOwner.Ananda,
  [Role.MultiConfigSettingsAdmin]: RoleOwner.Ananda,
  [Role.MultiConfigLanguagesManager]: RoleOwner.Ananda,
  [Role.MultiConfigPlatformsManager]: RoleOwner.Ananda,
  [Role.MultiConfigTerritoriesManager]: RoleOwner.Ananda,
  [Role.Multiverse]: RoleOwner.Ananda,
  [Role.MultiverseAdmin]: RoleOwner.Ananda,
  [Role.NoRole]: RoleOwner.Deprecated,
  [Role.Notifier]: RoleOwner.Deprecated,
  [Role.PackageEditor]: RoleOwner.Peder,
  [Role.PartnerIntegrationManager]: RoleOwner.Anton,
  [Role.PersonalizationSandboxUser]: RoleOwner.Anton,
  [Role.PPVManager]: RoleOwner.Peder,
  [Role.ProductLineEditor]: RoleOwner.Christy,
  [Role.ProductManager]: RoleOwner.Peder,
  [Role.RatingsManager]: RoleOwner.Christy,
  [Role.BulkRefundManager]: RoleOwner.Victoria,
  [Role.RARApprover]: RoleOwner.Peder,
  [Role.SegmentManager]: RoleOwner.Deepna,
  [Role.SiteBuilderManager]: RoleOwner.Christy,
  [Role.SiteConfigManager]: RoleOwner.Deepna,
  [Role.SonicDeveloper]: RoleOwner.Anton,
  [Role.SonicStudioBetaTester]: RoleOwner.Anton,
  [Role.StorageManager]: RoleOwner.Anton,
  [Role.StudioIdentityTester]: RoleOwner.Anton,
  [Role.StudioUserAdmin]: RoleOwner.Anton,
  [Role.TaxonomyManager]: RoleOwner.Christy,
  [Role.TemplateManager]: RoleOwner.Peder,
  [Role.TestSubscriptionsManager]: RoleOwner.Jonas,
  [Role.UsersAdmin]: RoleOwner.Shilan,
  [Role.UsersReader]: RoleOwner.Anton,
  [Role.WatchHistoryEditor]: RoleOwner.Anton, // It is unclear who will own it long term
  [Role.WatchHistoryReader]: RoleOwner.Anton,
});
